// DC location marker for the Lift Map.
//
// PBC could not tell which pins were at the DC during the active day, so the
// map shows a distinct DC icon at the selected DC's coordinates. The coordinates
// come from /v2/admin/locations (locationLatitude / locationLongitude), which the
// header already loads into redux as selectedLocationListData. Lift pin colors
// are not affected.

export const DC_MARKER_COLOR = "#2027A5";
export const DC_MARKER_SIZE = 34;

function toCoordinate(value, limit) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || Math.abs(n) > limit) return null;
  return n;
}

/**
 * Normalises a location object from the customer/locations tree into
 * { name, latitude, longitude }, or null when it has no usable coordinates.
 * 0,0 is treated as "not set" because that is how unset GPS values show up.
 */
export function getDcLocation(location) {
  if (!location) return null;
  const latitude = toCoordinate(location.locationLatitude ?? location.latitude, 90);
  const longitude = toCoordinate(location.locationLongitude ?? location.longitude, 180);
  if (latitude === null || longitude === null) return null;
  if (latitude === 0 && longitude === 0) return null;
  return {
    name: location.locationName || location.name || "DC",
    latitude,
    longitude,
    radiusMeters: toRadiusMeters(location.locationFenceRadiusMeters),
  };
}

/** Geofence radius in meters, or null when unset/invalid (no circle drawn). */
export function toRadiusMeters(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

// Geofence circle style: light fill so satellite imagery and pins stay readable.
export const DC_GEOFENCE_STYLE = {
  strokeColor: DC_MARKER_COLOR,
  strokeOpacity: 0.9,
  strokeWeight: 2,
  fillColor: DC_MARKER_COLOR,
  fillOpacity: 0.12,
};

/**
 * Finds the selected DC's location object.
 *
 * selectedLocationListData is authoritative. Routes.selectedLocation (the id) is
 * NOT: the reducer writes it once, in setCustomerAndLocations, as
 * `payload[0].locations[0].locationId` — the first location of the first
 * customer. Neither setSelectedCustomer nor setSelectedLocation updates it, and
 * the locationUpdate thunk behind the Location dropdown only sets
 * selectedLocationListData. So after any change of selection the id still points
 * at whatever DC happened to be first in the tree.
 *
 * An earlier version of this trusted the id and used the object only as a
 * shortcut, which put the wrong DC pin (and a geofence in the wrong place) on
 * the map whenever the user picked anything but that first location, and made
 * countLiftsAtDc report 0 because it name-matches lifts against the DC. Same
 * precedence as selectSelectedLocationId in hooks/useLiftDisplayPreference.js:
 * object first, id only as a fallback.
 */
export function findSelectedDcLocation(selectedLocationListData, customerAndLocationsData, selectedLocationId) {
  if (selectedLocationListData && selectedLocationListData.locationId != null) {
    return selectedLocationListData;
  }
  // No selection object yet (first render before the tree resolves). Fall back
  // to the id so the DC still plots, rather than showing no DC at all.
  if (selectedLocationId == null || selectedLocationId === "") {
    return selectedLocationListData || null;
  }
  const sameId = function (loc) {
    return loc && String(loc.locationId) === String(selectedLocationId);
  };
  for (const customer of customerAndLocationsData || []) {
    const match = (customer?.locations || []).find(sameId);
    if (match) return match;
  }
  return null;
}

/** Lifts the backend reports as inside this DC's geofence. */
export function countLiftsAtDc(liftList, dcName) {
  return (liftList || []).filter(function (lift) {
    return lift && lift.inDcGeofence === true && (!dcName || lift.dc === dcName);
  }).length;
}

/**
 * SVG for the DC icon: a blue rounded square with a white warehouse glyph.
 * Deliberately not a teardrop, so it can't be mistaken for a lift pin.
 */
export function buildDcIconSvg(size = DC_MARKER_SIZE) {
  return [
    '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + size + '" viewBox="0 0 34 34">',
    '<rect x="1.5" y="1.5" width="31" height="31" rx="7" fill="' + DC_MARKER_COLOR + '" stroke="#ffffff" stroke-width="2.5"/>',
    // roof
    '<path d="M7 15 L17 8 L27 15 Z" fill="#ffffff"/>',
    // building body with a dock door
    '<rect x="9" y="15" width="16" height="11" fill="#ffffff"/>',
    '<rect x="14" y="19" width="6" height="7" fill="' + DC_MARKER_COLOR + '"/>',
    "</svg>",
  ].join("");
}

export function buildDcIconUrl(size = DC_MARKER_SIZE) {
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(buildDcIconSvg(size));
}

/** Google Maps icon config. Anchored at the center, unlike the teardrop pins. */
export function buildDcMarkerIcon(size = DC_MARKER_SIZE) {
  return {
    url: buildDcIconUrl(size),
    scaledSize: { width: size, height: size },
    anchor: { x: size / 2, y: size / 2 },
  };
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
  });
}

/** InfoWindow content shown when the DC icon is clicked. */
export function buildDcInfoWindowContent(dc, liftsAtDc) {
  return (
    '<div style="font-family:Arial,sans-serif;font-size:13px;min-width:180px;line-height:1.8;">' +
    '<div style="font-weight:bold;font-size:15px;color:' + DC_MARKER_COLOR + ';margin-bottom:6px;">' +
    escapeHtml(dc.name) +
    "</div>" +
    "<div><b>Type:</b> Distribution Center</div>" +
    "<div><b>Lifts at DC:</b> " + liftsAtDc + "</div>" +
    (dc.radiusMeters
      ? "<div><b>Geofence:</b> " + Math.round(dc.radiusMeters) + " m radius</div>"
      : "") +
    "</div>"
  );
}
